import { GoogleAuthProvider, reauthenticateWithPopup } from 'firebase/auth';
import { auth } from '../../firebase/config';
import type { Contact } from '../../types';
import { normalizeContactInput } from '../../utils/contacts';

type PeopleResponse = {
  connections?: Array<{
    names?: Array<{ displayName?: string }>;
    phoneNumbers?: Array<{ canonicalForm?: string; value?: string }>;
    emailAddresses?: Array<{ value?: string }>;
  }>;
};

export async function fetchGoogleContactsPreview() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in first.');
  const hasGoogleProvider = user.providerData.some((provider) => provider.providerId === 'google.com');
  if (!hasGoogleProvider) throw new Error('Google Contacts import requires signing in with Google first.');

  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
  provider.setCustomParameters({ prompt: 'consent select_account', ...(user.email ? { login_hint: user.email } : {}) });
  let result;
  try {
    result = await reauthenticateWithPopup(user, provider);
  } catch (error: any) {
    const code = String(error?.code || '');
    if (code === 'auth/user-mismatch') throw new Error('Please select the same Google account used to sign in to PFS.');
    if (code === 'auth/popup-closed-by-user') throw new Error('Google Contacts import was cancelled before permission was granted.');
    if (code === 'auth/popup-blocked') throw new Error('Popup was blocked. Allow popups for PFS and try Import Google again.');
    if (code === 'auth/account-exists-with-different-credential') throw new Error('This Google account is linked to a different sign-in method. Sign out, sign in with the same Google account, then try again.');
    throw error;
  }
  if (result.user.uid !== user.uid || result.user.email !== user.email) throw new Error('Please select the same Google account used to sign in to PFS.');
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const accessToken = credential?.accessToken;
  if (!accessToken) throw new Error('Google did not return a Contacts access token. Check People API and OAuth consent setup.');

  const response = await fetch('https://people.googleapis.com/v1/people/me/connections?personFields=names,emailAddresses,phoneNumbers&pageSize=200', {
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!response.ok) {
    if (response.status === 403) throw new Error('Google People API is not enabled or contacts.readonly scope is not approved in OAuth consent.');
    if (response.status === 401) throw new Error('Google Contacts permission was missing or expired. Try Import Google again and approve Contacts access.');
    throw new Error(`Google People API failed: ${response.status}`);
  }
  const json = await response.json() as PeopleResponse;
  const contacts = (json.connections || [])
    .map((person): Partial<Contact> => normalizeContactInput({
      name: person.names?.[0]?.displayName || '',
      phone: person.phoneNumbers?.[0]?.canonicalForm || person.phoneNumbers?.[0]?.value || '',
      email: person.emailAddresses?.[0]?.value || '',
      source: 'google',
      active: true
    }))
    .filter((contact) => contact.name || contact.phone || contact.email);
  if (!contacts.length) throw new Error('No Google Contacts found. For iPhone contacts, sync iPhone Contacts to Google Contacts first, then import.');
  return contacts;
}
